import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "grabber_pwa_dismissed";

export default function PWAInstall() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Register SW once
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
      // Auto-reload once when a NEW service worker replaces an old one (fresh app version)
      let hadController = !!navigator.serviceWorker.controller;
      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!hadController) { hadController = true; return; }
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    }
    if (localStorage.getItem(DISMISS_KEY)) return;

    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      // Show after small delay
      setTimeout(() => setVisible(true), 8000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    setVisible(false);
    setPrompt(null);
    localStorage.setItem(DISMISS_KEY, "installed");
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "dismissed");
  };

  return (
    <AnimatePresence>
      {visible && prompt && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="fixed right-4 bottom-20 md:bottom-6 z-30 max-w-xs"
          data-testid="pwa-install-banner"
        >
          <div className="glass rounded-2xl p-4 shadow-xl shadow-foreground/10 border border-primary/20 relative">
            <button
              onClick={dismiss}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-background border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground shadow-sm"
              data-testid="pwa-dismiss"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">Installer Grabber</p>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">Tilføj til din skærm for hurtigere adgang.</p>
                <Button size="sm" onClick={install} className="rounded-lg gap-1.5 h-8 text-xs" data-testid="pwa-install-btn">
                  <Download className="w-3.5 h-3.5" /> Installer
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
